import { getAllComponents } from '../data/aiOntology';
import { UNIVERSAL_INDUSTRIES, UNIVERSAL_DATA_TYPES, UNIVERSAL_PROBLEM_TYPES } from '../data/universalIndustries';
import { alluvialMapping, getExactApproach, getApproachLayers } from '../data/alluvialMapping';
import { SOLUTION_SIZING } from '../data/solutionSizing';
import { COMPONENT_GROUPINGS } from '../data/componentGroupings';

// ============================================================
// PROBLEM TYPE → COMPONENT MAPPING (Complete)
// ============================================================

const PROBLEM_TYPE_COMPONENT_MAPPING = {
  classification: {
    essential: ['XGBoost', 'Random Forest', 'Cross-Entropy', 'Adam', 'One-Hot Encoding'],
    recommended: ['Logistic Regression', 'SVM', 'SHAP', 'LIME'],
    dataProcessing: ['Normalization', 'Great Expectations', 'One-Hot Encoding'],
    infrastructure: ['CPU (x86)', 'DRAM', 'SSD (SATA)'],
    approaches: [1, 7, 8]
  },
  regression: {
    essential: ['Linear Regression', 'XGBoost', 'MSE', 'Adam', 'Normalization'],
    recommended: ['Random Forest', 'SHAP', 'Feature Engineering'],
    dataProcessing: ['Normalization', 'Outlier Removal'],
    infrastructure: ['CPU (x86)', 'DRAM', 'SSD (SATA)'],
    approaches: [1, 7, 27]
  },
  forecasting: {
    essential: ['LSTM', 'Transformer', 'MSE', 'Adam'],
    recommended: ['XGBoost', 'SHAP', 'Time Series Analysis'],
    dataProcessing: ['Time Series Decomposition', 'Normalization'],
    infrastructure: ['GPU (NVIDIA)', 'DRAM', 'SSD (NVMe)'],
    approaches: [1, 27, 36]
  },
  anomaly: {
    essential: ['XGBoost', 'Isolation Forest', 'Adam'],
    recommended: ['LSTM', 'SHAP', 'Autoencoder'],
    dataProcessing: ['Normalization', 'Outlier Removal'],
    infrastructure: ['GPU (NVIDIA)', 'DRAM', 'SSD (NVMe)'],
    approaches: [1, 12, 39]
  },
  recommendation: {
    essential: ['K-Nearest Neighbors', 'Embedding Generation'],
    recommended: ['A/B Testing', 'Feature Flags', 'Recommendation Engine'],
    dataProcessing: ['Embedding Generation', 'Normalization'],
    infrastructure: ['GPU (NVIDIA)', 'DRAM', 'Redis'],
    approaches: [4, 37, 8]
  },
  nlp: {
    essential: ['Transformer', 'Tokenization (BPE)', 'Embedding Generation', 'Cross-Entropy'],
    recommended: ['Prompt Templates', 'RAG Pipelines', 'Sentiment Analysis'],
    dataProcessing: ['Tokenization', 'Embedding Generation'],
    infrastructure: ['GPU (NVIDIA)', 'HBM (High Bandwidth Memory)', 'SSD (NVMe)'],
    approaches: [4, 37, 1]
  },
  vision: {
    essential: ['CNN', 'Image Augmentation', 'Cross-Entropy', 'Adam'],
    recommended: ['Transfer Learning', 'Object Detection', 'Image Augmentation'],
    dataProcessing: ['Image Augmentation', 'Normalization'],
    infrastructure: ['GPU (NVIDIA)', 'HBM (High Bandwidth Memory)', 'SSD (NVMe)', 'Liquid Cooling'],
    approaches: [1, 28, 12]
  },
  generation: {
    essential: ['Diffusion Model', 'GAN', 'Adam'],
    recommended: ['VAE', 'Prompt Templates', 'Transformer'],
    dataProcessing: ['Data Augmentation', 'Normalization'],
    infrastructure: ['GPU (NVIDIA)', 'HBM (High Bandwidth Memory)', 'SSD (NVMe)', 'Liquid Cooling'],
    approaches: [4, 1, 40]
  },
  segmentation: {
    essential: ['PCA', 'K-Means'],
    recommended: ['t-SNE', 'DBSCAN'],
    dataProcessing: ['Normalization', 'Dimensionality Reduction'],
    infrastructure: ['CPU (x86)', 'DRAM', 'SSD (SATA)'],
    approaches: [7, 1, 4]
  },
  optimization: {
    essential: ['Genetic Algorithm', 'Simulated Annealing'],
    recommended: ['Particle Swarm', 'Linear Programming'],
    dataProcessing: ['Feature Engineering', 'Normalization'],
    infrastructure: ['CPU (x86)', 'DRAM', 'SSD (SATA)'],
    approaches: [1, 27, 36]
  },
  sentiment: {
    essential: ['Transformer', 'Tokenization (BPE)', 'Cross-Entropy'],
    recommended: ['Sentiment Analysis', 'Named Entity Recognition'],
    dataProcessing: ['Tokenization', 'Embedding Generation'],
    infrastructure: ['GPU (NVIDIA)', 'DRAM', 'SSD (NVMe)'],
    approaches: [4, 37, 31]
  },
  ranking: {
    essential: ['XGBoost', 'Learning to Rank'],
    recommended: ['Feature Engineering'],
    dataProcessing: ['Feature Engineering', 'Normalization'],
    infrastructure: ['CPU (x86)', 'DRAM', 'SSD (SATA)'],
    approaches: [1, 7, 4]
  },
  chatbot: {
    essential: ['Transformer', 'Tokenization (BPE)', 'Chatbot', 'RLHF Pipeline'],
    recommended: ['Prompt Templates', 'Guardrails', 'Safety Classifiers'],
    dataProcessing: ['Tokenization', 'Embedding Generation', 'Prompt Templates'],
    infrastructure: ['GPU (NVIDIA)', 'HBM (High Bandwidth Memory)', 'SSD (NVMe)'],
    approaches: [37, 4, 14]
  },
  search: {
    essential: ['Embedding Generation', 'Vector Search'],
    recommended: ['Semantic Search', 'Ranking'],
    dataProcessing: ['Embedding Generation', 'Tokenization'],
    infrastructure: ['CPU (x86)', 'DRAM', 'SSD (NVMe)', 'Redis'],
    approaches: [4, 7, 1]
  },
  extraction: {
    essential: ['Transformer', 'Tokenization (BPE)'],
    recommended: ['Named Entity Recognition', 'Information Extraction'],
    dataProcessing: ['Tokenization', 'Text Preprocessing'],
    infrastructure: ['GPU (NVIDIA)', 'DRAM', 'SSD (NVMe)'],
    approaches: [4, 7, 1]
  },
  translation: {
    essential: ['Transformer', 'Tokenization (BPE)'],
    recommended: ['Sequence-to-Sequence', 'Attention Mechanism'],
    dataProcessing: ['Tokenization', 'Text Preprocessing'],
    infrastructure: ['GPU (NVIDIA)', 'HBM (High Bandwidth Memory)'],
    approaches: [4, 37, 1]
  },
  summarization: {
    essential: ['Transformer', 'Tokenization (BPE)'],
    recommended: ['Text Summarization'],
    dataProcessing: ['Tokenization', 'Text Preprocessing'],
    infrastructure: ['GPU (NVIDIA)', 'DRAM', 'SSD (NVMe)'],
    approaches: [4, 37, 1]
  },
  reinforcement: {
    essential: ['PPO', 'DQN', 'Actor-Critic Methods'],
    recommended: ['A3C', 'Reward Modeling'],
    dataProcessing: ['Environment Setup', 'State Encoding'],
    infrastructure: ['GPU (NVIDIA)', 'DRAM', 'SSD (NVMe)'],
    approaches: [1, 14, 32]
  },
  planning: {
    essential: ['A* Search', 'Monte Carlo Tree Search'],
    recommended: ['Genetic Algorithm', 'Constraint Programming'],
    dataProcessing: ['State Space Definition'],
    infrastructure: ['CPU (x86)', 'DRAM'],
    approaches: [27, 36, 14]
  },
  detection: {
    essential: ['CNN', 'Image Augmentation'],
    recommended: ['Object Detection', 'Transfer Learning'],
    dataProcessing: ['Image Augmentation', 'Normalization'],
    infrastructure: ['GPU (NVIDIA)', 'HBM (High Bandwidth Memory)', 'SSD (NVMe)'],
    approaches: [1, 28, 12]
  },
  diagnosis: {
    essential: ['CNN', 'XGBoost', 'SHAP'],
    recommended: ['Transfer Learning', 'Explainable AI'],
    dataProcessing: ['Image Augmentation', 'Normalization'],
    infrastructure: ['GPU (NVIDIA)', 'DRAM', 'SSD (NVMe)'],
    approaches: [1, 15, 34]
  },
  risk: {
    essential: ['XGBoost', 'Random Forest', 'SHAP'],
    recommended: ['Monte Carlo Simulation', 'Bayesian Networks'],
    dataProcessing: ['Feature Engineering', 'Normalization'],
    infrastructure: ['CPU (x86)', 'DRAM', 'SSD (SATA)'],
    approaches: [27, 1, 30]
  },
  fraud: {
    essential: ['XGBoost', 'Isolation Forest', 'Apache Kafka'],
    recommended: ['Random Forest', 'Graph Analysis'],
    dataProcessing: ['Feature Engineering', 'Normalization', 'Streaming Processing'],
    infrastructure: ['GPU (NVIDIA)', 'DRAM', 'SSD (NVMe)', 'Apache Kafka'],
    approaches: [1, 12, 24]
  },
  personalization: {
    essential: ['Embedding Generation', 'Recommendation Engine'],
    recommended: ['A/B Testing', 'Feature Flags'],
    dataProcessing: ['Embedding Generation', 'Feature Engineering'],
    infrastructure: ['GPU (NVIDIA)', 'DRAM', 'Redis'],
    approaches: [4, 37, 8]
  },
  automation: {
    essential: ['Workflow Automation', 'Rule Engine'],
    recommended: ['RPA', 'Process Mining'],
    dataProcessing: ['Data Validation', 'ETL Pipeline'],
    infrastructure: ['CPU (x86)', 'DRAM', 'SSD (SATA)'],
    approaches: [39, 14, 7]
  },
  monitoring: {
    essential: ['Prometheus', 'Grafana', 'Log Aggregation (ELK)'],
    recommended: ['OpenTelemetry', 'Sentry'],
    dataProcessing: ['Log Processing', 'Metrics Collection'],
    infrastructure: ['CPU (x86)', 'DRAM', 'SSD (SATA)'],
    approaches: [39, 12, 29]
  },
  quality: {
    essential: ['CNN', 'Image Augmentation'],
    recommended: ['Transfer Learning', 'Defect Detection'],
    dataProcessing: ['Image Augmentation', 'Normalization'],
    infrastructure: ['GPU (NVIDIA)', 'DRAM', 'SSD (NVMe)'],
    approaches: [1, 28, 22]
  },
  maintenance: {
    essential: ['LSTM', 'Apache Kafka'],
    recommended: ['XGBoost', 'Sensor Analytics'],
    dataProcessing: ['Time Series Decomposition', 'Feature Engineering'],
    infrastructure: ['GPU (NVIDIA)', 'DRAM', 'SSD (NVMe)', 'IoT Sensors'],
    approaches: [12, 36, 29]
  },
  routing: {
    essential: ['A* Search', 'Graph Neural Network'],
    recommended: ['Genetic Algorithm', 'Ant Colony'],
    dataProcessing: ['Graph Construction', 'Spatial Data Processing'],
    infrastructure: ['CPU (x86)', 'DRAM', 'SSD (SATA)'],
    approaches: [12, 27, 14]
  },
  matching: {
    essential: ['Embedding Generation', 'Similarity Matching'],
    recommended: ['Neural Matching', 'Feature Engineering'],
    dataProcessing: ['Embedding Generation', 'Feature Engineering'],
    infrastructure: ['CPU (x86)', 'DRAM', 'SSD (SATA)'],
    approaches: [7, 4, 1]
  }
};

// ============================================================
// DATA TYPE → COMPONENT MAPPING (Complete)
// ============================================================

const DATA_TYPE_COMPONENT_MAPPING = {
  'structured': {
    storage: ['PostgreSQL', 'MySQL'],
    processing: ['SQL SELECT Queries', 'ETL Pipeline (Airflow)', 'Great Expectations'],
    infrastructure: ['SSD (SATA)']
  },
  'unstructured': {
    storage: ['AWS S3', 'MongoDB'],
    processing: ['ETL Pipeline (Airflow)', 'Document Processing'],
    infrastructure: ['SSD (NVMe)']
  },
  'image': {
    storage: ['AWS S3'],
    processing: ['Image Rotation/Cropping', 'CNN'],
    infrastructure: ['GPU (NVIDIA)', 'SSD (NVMe)']
  },
  'video': {
    storage: ['AWS S3'],
    processing: ['Video Processing', 'Frame Extraction'],
    infrastructure: ['GPU (NVIDIA)', 'HBM (High Bandwidth Memory)', 'SSD (NVMe)']
  },
  'audio': {
    storage: ['AWS S3'],
    processing: ['Audio Processing', 'Speech Recognition'],
    infrastructure: ['GPU (NVIDIA)', 'SSD (NVMe)']
  },
  'text': {
    storage: ['PostgreSQL', 'Pinecone'],
    processing: ['Tokenization (BPE)', 'Embedding Generation'],
    infrastructure: ['GPU (NVIDIA)', 'SSD (NVMe)']
  },
  'sensor': {
    storage: ['InfluxDB'],
    processing: ['Stream Processing', 'Apache Kafka'],
    infrastructure: ['IoT Sensors', 'Edge Devices', 'Apache Kafka']
  },
  'time-series': {
    storage: ['InfluxDB', 'TimescaleDB', 'Prometheus'],
    processing: ['Time Series Analysis', 'Forecasting'],
    infrastructure: ['SSD (NVMe)', 'Apache Kafka']
  },
  'geospatial': {
    storage: ['PostgreSQL'],
    processing: ['Spatial Analysis', 'Map Processing'],
    infrastructure: ['SSD (SATA)']
  },
  'graph': {
    storage: ['Neo4j'],
    processing: ['Graph Analysis', 'Network Analysis'],
    infrastructure: ['DRAM', 'SSD (NVMe)']
  },
  'streaming': {
    storage: ['Apache Kafka', 'Redis Streams'],
    processing: ['Stream Processing', 'Real-time Analytics'],
    infrastructure: ['SSD (NVMe)', 'Apache Kafka']
  },
  'genomic': {
    storage: ['AWS S3'],
    processing: ['Bioinformatics Pipeline', 'Sequence Analysis'],
    infrastructure: ['GPU (NVIDIA)', 'HBM (High Bandwidth Memory)']
  },
  'chemical': {
    storage: ['Chemical Database'],
    processing: ['Molecular Analysis', 'Compound Screening'],
    infrastructure: ['GPU (NVIDIA)', 'SSD (NVMe)']
  },
  'biometric': {
    storage: ['Secure Storage'],
    processing: ['Biometric Processing', 'Pattern Matching'],
    infrastructure: ['GPU (NVIDIA)', 'SSD (NVMe)']
  },
  'financial': {
    storage: ['PostgreSQL'],
    processing: ['Transaction Processing', 'Risk Analysis'],
    infrastructure: ['SSD (NVMe)', 'Redis']
  },
  'medical': {
    storage: ['HIPAA Compliant Storage'],
    processing: ['Clinical Data Processing', 'Medical Imaging'],
    infrastructure: ['GPU (NVIDIA)', 'SSD (NVMe)']
  },
  'behavioral': {
    storage: ['Event Database'],
    processing: ['Behavior Analysis', 'Pattern Recognition'],
    infrastructure: ['SSD (NVMe)', 'Redis']
  },
  'social': {
    storage: ['NoSQL Database'],
    processing: ['Social Network Analysis', 'Sentiment Analysis'],
    infrastructure: ['GPU (NVIDIA)', 'SSD (NVMe)']
  },
  'log': {
    storage: ['Elasticsearch'],
    processing: ['Log Processing', 'Anomaly Detection'],
    infrastructure: ['SSD (SATA)', 'Apache Kafka']
  },
  'document': {
    storage: ['MongoDB'],
    processing: ['OCR', 'Document Processing', 'NLP'],
    infrastructure: ['GPU (NVIDIA)', 'SSD (NVMe)']
  },
  'satellite': {
    storage: ['AWS S3'],
    processing: ['Satellite Image Processing', 'Remote Sensing'],
    infrastructure: ['GPU (NVIDIA)', 'HBM (High Bandwidth Memory)']
  },
  'weather': {
    storage: ['Time-Series Database'],
    processing: ['Weather Analysis', 'Climate Modeling'],
    infrastructure: ['GPU (NVIDIA)', 'SSD (NVMe)']
  },
  'market': {
    storage: ['Time-Series Database'],
    processing: ['Market Analysis', 'Price Prediction'],
    infrastructure: ['SSD (NVMe)', 'Redis']
  },
  'customer': {
    storage: ['PostgreSQL', 'CRM'],
    processing: ['Customer Analytics', 'Segmentation'],
    infrastructure: ['SSD (SATA)']
  },
  'transaction': {
    storage: ['PostgreSQL'],
    processing: ['Transaction Processing', 'Fraud Detection'],
    infrastructure: ['SSD (NVMe)', 'Redis', 'Apache Kafka']
  },
  'inventory': {
    storage: ['PostgreSQL'],
    processing: ['Inventory Optimization', 'Demand Forecasting'],
    infrastructure: ['SSD (SATA)']
  },
  'equipment': {
    storage: ['Time-Series Database'],
    processing: ['Predictive Maintenance', 'Sensor Analytics'],
    infrastructure: ['IoT Sensors', 'Edge Devices', 'Apache Kafka']
  },
  'quality': {
    storage: ['Quality Database'],
    processing: ['Quality Analysis', 'Defect Detection'],
    infrastructure: ['GPU (NVIDIA)', 'SSD (NVMe)']
  },
  'safety': {
    storage: ['Incident Database'],
    processing: ['Safety Analysis', 'Risk Assessment'],
    infrastructure: ['SSD (SATA)']
  },
  'energy': {
    storage: ['Time-Series Database'],
    processing: ['Energy Analysis', 'Consumption Forecasting'],
    infrastructure: ['IoT Sensors', 'Apache Kafka']
  },
  'environmental': {
    storage: ['Environmental Database'],
    processing: ['Environmental Analysis', 'Pollution Detection'],
    infrastructure: ['IoT Sensors', 'Edge Devices']
  },
  'legal': {
    storage: ['Document Database'],
    processing: ['Legal Document Processing', 'Contract Analysis'],
    infrastructure: ['GPU (NVIDIA)', 'SSD (NVMe)']
  },
  'educational': {
    storage: ['PostgreSQL'],
    processing: ['Learning Analytics', 'Student Analysis'],
    infrastructure: ['SSD (SATA)']
  },
  'agricultural': {
    storage: ['Sensor Database'],
    processing: ['Crop Analysis', 'Yield Prediction'],
    infrastructure: ['IoT Sensors', 'Edge Devices']
  },
  'telecom': {
    storage: ['Time-Series Database'],
    processing: ['Network Analysis', 'Call Analytics'],
    infrastructure: ['SSD (NVMe)', 'Apache Kafka']
  },
  'media': {
    storage: ['AWS S3'],
    processing: ['Media Processing', 'Content Analysis'],
    infrastructure: ['GPU (NVIDIA)', 'SSD (NVMe)']
  },
  'game': {
    storage: ['Game Database'],
    processing: ['Game Analytics', 'Player Behavior'],
    infrastructure: ['GPU (NVIDIA)', 'SSD (NVMe)']
  },
  'supply-chain': {
    storage: ['Supply Chain Database'],
    processing: ['Supply Chain Optimization', 'Logistics Analysis'],
    infrastructure: ['SSD (SATA)']
  },
  'hr': {
    storage: ['HR Database'],
    processing: ['HR Analytics', 'Talent Analysis'],
    infrastructure: ['SSD (SATA)']
  },
  'real-estate': {
    storage: ['Property Database'],
    processing: ['Property Analysis', 'Valuation'],
    infrastructure: ['SSD (SATA)']
  }
};

// ============================================================
// INDUSTRY → APPROACH & COMPONENT MAPPING (Complete)
// ============================================================

const INDUSTRY_MAPPING = {
  'healthcare': {
    approaches: [1, 30, 24],
    components: ['HIPAA Safeguards', 'Doctor-in-the-loop Dashboard', 'Medical Imaging Analysis'],
    compliance: 'high',
    securityLevel: 'enhanced'
  },
  'finance': {
    approaches: [1, 30, 11],
    components: ['SOC2 Compliance', 'Audit Logs (SIEM)', 'Fraud Detection', 'Real-time Processing'],
    compliance: 'high',
    securityLevel: 'critical'
  },
  'retail': {
    approaches: [8, 37, 12],
    components: ['Recommendation Carousel', 'A/B Testing Frame', 'Dynamic Pricing'],
    compliance: 'medium',
    securityLevel: 'standard'
  },
  'manufacturing': {
    approaches: [12, 36, 39],
    components: ['Predictive Maintenance', 'Quality Inspection', 'IoT Sensors'],
    compliance: 'medium',
    securityLevel: 'standard'
  },
  'technology': {
    approaches: [1, 7, 40],
    components: ['CI/CD Pipeline', 'Kubernetes', 'API Gateway'],
    compliance: 'medium',
    securityLevel: 'standard'
  },
  'energy': {
    approaches: [12, 36, 39],
    components: ['Predictive Maintenance', 'IoT Sensors', 'Time Series Analysis'],
    compliance: 'medium',
    securityLevel: 'enhanced'
  },
  'transport': {
    approaches: [12, 14, 32],
    components: ['Route Optimization', 'GPS Tracking', 'Fleet Management'],
    compliance: 'medium',
    securityLevel: 'standard'
  },
  'agriculture': {
    approaches: [12, 26, 34],
    components: ['IoT Sensors', 'Satellite Image Analysis', 'Weather Analysis'],
    compliance: 'low',
    securityLevel: 'standard'
  },
  'education': {
    approaches: [37, 4, 2],
    components: ['Personalized Learning', 'Chatbot', 'Content Generation'],
    compliance: 'medium',
    securityLevel: 'standard'
  },
  'telecom': {
    approaches: [39, 12, 29],
    components: ['Network Optimization', 'Churn Prediction', 'Anomaly Detection'],
    compliance: 'medium',
    securityLevel: 'standard'
  },
  'media': {
    approaches: [37, 28, 4],
    components: ['Content Recommendations', 'Sentiment Analysis', 'Content Generation'],
    compliance: 'medium',
    securityLevel: 'standard'
  },
  'government': {
    approaches: [30, 11, 19],
    components: ['Document Processing', 'Citizen Service Automation', 'Compliance Monitoring'],
    compliance: 'high',
    securityLevel: 'critical'
  },
  'construction': {
    approaches: [36, 12, 32],
    components: ['Safety Monitoring', 'Quality Inspection', 'Computer Vision'],
    compliance: 'medium',
    securityLevel: 'enhanced'
  },
  'hospitality': {
    approaches: [37, 2, 4],
    components: ['Dynamic Pricing', 'Recommendation Engine', 'Sentiment Analysis'],
    compliance: 'medium',
    securityLevel: 'standard'
  },
  'professional': {
    approaches: [4, 7, 37],
    components: ['Document Automation', 'Contract Analysis', 'Client Analytics'],
    compliance: 'medium',
    securityLevel: 'standard'
  },
  'sports': {
    approaches: [12, 36, 29],
    components: ['Performance Analytics', 'Wearable Analytics', 'Injury Prediction'],
    compliance: 'low',
    securityLevel: 'standard'
  },
  'automotive': {
    approaches: [32, 12, 22],
    components: ['Autonomous Driving', 'Predictive Maintenance', 'Computer Vision'],
    compliance: 'high',
    securityLevel: 'critical'
  },
  'insurance': {
    approaches: [1, 30, 27],
    components: ['Underwriting Automation', 'Fraud Detection', 'Risk Assessment'],
    compliance: 'high',
    securityLevel: 'enhanced'
  },
  'pharma': {
    approaches: [34, 1, 26],
    components: ['Drug Discovery', 'Clinical Trial Optimization', 'Regulatory Compliance'],
    compliance: 'high',
    securityLevel: 'enhanced'
  },
  'environmental': {
    approaches: [34, 12, 20],
    components: ['Climate Prediction', 'Pollution Detection', 'Satellite Analysis'],
    compliance: 'medium',
    securityLevel: 'standard'
  },
  'legal': {
    approaches: [37, 4, 7],
    components: ['Document Analysis', 'Contract Review', 'Case Prediction'],
    compliance: 'high',
    securityLevel: 'enhanced'
  },
  'aerospace': {
    approaches: [32, 12, 36],
    components: ['Predictive Maintenance', 'Flight Optimization', 'Satellite Analysis'],
    compliance: 'high',
    securityLevel: 'critical'
  },
  'crossindustry': {
    approaches: [8, 7, 4],
    components: [],
    compliance: 'medium',
    securityLevel: 'standard'
  }
};

// ============================================================
// ENHANCED ARCHITECTURE GENERATOR - WOW MOMENTS INCLUDED
// ============================================================

export const generateUniversalArchitecture = (requirements) => {
  const allComponents = getAllComponents();
  const architecture = {
    industryContext: {},
    selectedComponents: [],
    selectedApproaches: [],
    componentSpecifications: {},
    solutionSizing: {},
    costModel: {},
    recommendations: [],
    risks: [],
    roadmap: [],
    kpis: [],
    summary: {},
    alignmentMatrix: {},
    traceabilityMap: {},
    groupingData: {}
  };
  
  const {
	solutionMeta = {},
    industryDetails = {},
    scalingDetails = {},
    //sizingInputs = {},  
    industry, problems, problemTypes, dataTypes,
    scale, latency, budget, expertise, deployment,
    compliance, timeframe, priority, dataVolume,
    securityLevel, integrationNeeds
  } = requirements;
  
  // ==================== INDUSTRY CONTEXT ====================
  const industryData = UNIVERSAL_INDUSTRIES[industry] || UNIVERSAL_INDUSTRIES.general;
  const industryMapping = INDUSTRY_MAPPING[industry] || INDUSTRY_MAPPING.crossindustry;
  
  architecture.industryContext = {
    industryName: industryData?.name || 'General',
    problems: problems?.map(pId => industryData?.problems?.find(p => p.id === pId)?.name).filter(Boolean),
    problemTypes,
    dataTypes,
    recommendedApproaches: industryMapping.approaches,
    autoCompliance: industryMapping.compliance,
    autoSecurityLevel: industryMapping.securityLevel
  };
  
  // ==================== APPROACH SELECTION ====================
  const approaches = new Set();
  
  // From industry mapping
  industryMapping.approaches.forEach(a => approaches.add(a));
  
  // From problem types
  problemTypes?.forEach(pt => {
    const mapping = PROBLEM_TYPE_COMPONENT_MAPPING[pt];
    if (mapping?.approaches) {
      mapping.approaches.forEach(a => approaches.add(a));
    }
  });
  
  // From getExactApproach for each requirement category
  const reqCategories = [
    'business-value', 'data-processing', 'model-inference',
    'scalable-infrastructure', 'data-storage', 'data-protection',
    'real-time-processing', 'regulatory-compliance', 'monitoring-observability'
  ];
  
  reqCategories.forEach(reqCat => {
    const approach = getExactApproach(reqCat, requirements);
    if (approach?.approachId) {
      approaches.add(approach.approachId);
    }
  });
  
  architecture.selectedApproaches = Array.from(approaches);
  
  // ==================== COMPONENT SELECTION ====================
  const selectedComponentsSet = new Set();
  
  const addComponent = (componentName) => {
    const comp = allComponents.find(c => c.name === componentName);
    if (comp && !selectedComponentsSet.has(comp.id)) {
      selectedComponentsSet.add(comp.id);
      architecture.selectedComponents.push({
        ...comp,
        approachId: getBestApproachForComponent(componentName, approaches),
        quantity: 1
      });
    }
  };
  
  // 1. From Problem Types
  problemTypes?.forEach(pt => {
    const mapping = PROBLEM_TYPE_COMPONENT_MAPPING[pt];
    if (mapping) {
      mapping.essential?.forEach(comp => addComponent(comp));
      if (budget !== 'low') {
        mapping.recommended?.forEach(comp => addComponent(comp));
      }
      mapping.dataProcessing?.forEach(comp => addComponent(comp));
      mapping.infrastructure?.forEach(comp => addComponent(comp));
    }
  });
  
  // 2. From Data Types
  dataTypes?.forEach(dt => {
    const mapping = DATA_TYPE_COMPONENT_MAPPING[dt];
    if (mapping) {
      mapping.storage?.forEach(comp => addComponent(comp));
      mapping.processing?.forEach(comp => addComponent(comp));
      mapping.infrastructure?.forEach(comp => addComponent(comp));
    }
  });
  
  // 3. From Industry
  industryMapping.components.forEach(comp => addComponent(comp));
  
  // 4. From Scale
  if (scale === 'enterprise') {
    ['Kubernetes', 'Apache Airflow', 'Datadog', 'SSO (Okta)', 'Load Balancer', 'InfiniBand'].forEach(addComponent);
  } else if (scale === 'smb') {
    ['Docker', 'GitHub Actions', 'Prometheus'].forEach(addComponent);
  }
  
  // 5. From Latency
  if (latency === 'real-time' || latency === 'ultra-low') {
    ['Apache Kafka', 'Redis', 'WebSocket'].forEach(addComponent);
  }
  if (latency === 'ultra-low') {
    ['FPGA', 'NVLink'].forEach(addComponent);
  }
  
  // 6. From Deployment
  if (deployment === 'cloud') {
    ['AWS ECS', 'CloudWatch', 'AWS S3'].forEach(addComponent);
  } else if (deployment === 'edge') {
    ['On-device Execution', 'Quantization (INT8)'].forEach(addComponent);
  } else if (deployment === 'on-premise') {
    ['Air Cooling', 'Uninterruptible Power Supplies'].forEach(addComponent);
  }
  
  // 7. From Compliance
  if (compliance === 'high') {
    ['Audit Logs (SIEM)', 'Data Encryption (TLS/AES)', 'WAF', 'RBAC', 'Bias Audit Reports', 'Model Cards', 'Data Cards'].forEach(addComponent);
  } else if (compliance === 'medium') {
    ['Data Encryption (TLS/AES)', 'OAuth 2.0', 'RBAC'].forEach(addComponent);
  }
  
  // 8. From Security
  if (securityLevel === 'critical') {
    ['Prompt Injection Firewall', 'Adversarial Training', 'Network Segmentation', 'Air-gapping'].forEach(addComponent);
  } else if (securityLevel === 'enhanced') {
    ['Rate Limiting', 'WAF', 'Adversarial Training'].forEach(addComponent);
  }
  
  // 9. From Priority
  if (priority === 'quality') {
    ['Evidently AI', 'WhyLabs', 'SHAP', 'LIME', 'Great Expectations'].forEach(addComponent);
  } else if (priority === 'cost') {
    ['FinOps Budgets', 'Quantization (FP16)'].forEach(addComponent);
  } else if (priority === 'speed') {
    ['TensorRT', 'ONNX Runtime', 'Redis'].forEach(addComponent);
  }
  
  // 10. From Expertise
  if (expertise === 'expert') {
    ['Kubeflow Pipelines', 'MLflow Registry', 'DVC'].forEach(addComponent);
  } else if (expertise === 'beginner') {
    ['AutoML', 'Streamlit', 'Gradio', 'Scikit-learn'].forEach(addComponent);
  }
  
  // 11. From Data Volume
  if (dataVolume === 'massive') {
    ['Apache Spark', 'Tape Drive', 'HBM (High Bandwidth Memory)'].forEach(addComponent);
  } else if (dataVolume === 'large') {
    ['Apache Kafka', 'AWS S3', 'SSD (NVMe)'].forEach(addComponent);
  }
  
  // 12. From Integration
  integrationNeeds?.forEach(integration => {
    if (integration === 'CRM') addComponent('REST API');
    if (integration === 'ERP') addComponent('gRPC');
    if (integration === 'Cloud Storage') addComponent('AWS S3');
    if (integration === 'Email') addComponent('Webhook');
    if (integration === 'Payment Gateway') addComponent('Data Encryption (TLS/AES)');
  });
  
  // 13. Always Required
  ['Prometheus', 'Grafana', 'Log Aggregation (ELK)', 'OAuth 2.0', 'Data Encryption (TLS/AES)', 'Model Cards', 'Git Version History'].forEach(addComponent);
  
  // ==================== GENERATE COMPONENT SPECIFICATIONS ====================
  const sizingInputs = {
    scale, dataVolume, tps: latency === 'real-time' ? 'high' : 'medium',
    availability: compliance === 'high' ? 'critical' : 'high',
    latency, problemTypes, dataTypes, budget, deployment,
    compliance, securityLevel, expertise, industry, priority
  };
  
  try {
    const specs = SOLUTION_SIZING.generateSolutionSpecification(
      architecture.selectedComponents,
      requirements,
      sizingInputs
    );
    architecture.componentSpecifications = specs.componentSpecifications || {};
    architecture.solutionSizing = specs;
  } catch (error) {
    console.warn('Specification generation skipped:', error.message);
  }
  
  // ==================== GROUPING DATA ====================
  architecture.groupingData = {
    typeGroups: COMPONENT_GROUPINGS.typeGroups,
    sequenceGroups: COMPONENT_GROUPINGS.sequenceGroups
  };
  
  // ==================== COST MODEL ====================
  architecture.costModel = calculateCost(architecture.selectedComponents);
  
  // ==================== RECOMMENDATIONS ====================
  architecture.recommendations = generateRecommendations(requirements, architecture);
  
  // ==================== RISKS ====================
  architecture.risks = identifyRisks(requirements, architecture);
  
  // ==================== ROADMAP ====================
  architecture.roadmap = createRoadmap(requirements);
  
  // ==================== KPIs ====================
  architecture.kpis = defineKPIs(requirements);
  
  architecture.solutionMeta = solutionMeta;
  architecture.industryDetails = industryDetails;
  architecture.scalingDetails = scalingDetails;
  architecture.sizingInputs = sizingInputs;
  
    // Use sizing inputs for specifications if available
  if (sizingInputs.expectedUsers) {
    sizingInputs.users = sizingInputs.expectedUsers;
  }
  
  // ==================== SUMMARY ====================
  architecture.summary = {
    totalComponents: architecture.selectedComponents.length,
    layersCovered: new Set(architecture.selectedComponents.map(c => c.layerId)).size,
    phasesCovered: new Set(architecture.selectedComponents.map(c => c.phaseId)).size,
    subLayersCovered: new Set(architecture.selectedComponents.map(c => c.subLayerId)).size,
    totalApproaches: approaches.size,
    estimatedCost: architecture.costModel,
    alignmentScore: calculateAlignmentScore(requirements, architecture.selectedComponents),
    confidence: calculateConfidence(requirements),
    completenessScore: calculateCompletenessScore(requirements, architecture.selectedComponents),
    dataCoverageScore: calculateDataCoverageScore(dataTypes, architecture.selectedComponents)
  };
  
  return architecture;
};

// ============================================================
// HELPER FUNCTIONS
// ============================================================

function getBestApproachForComponent(componentName, approaches) {
  // Simple mapping - find which approach category this component belongs to
  const componentToApproach = {
    'CPU': 1, 'GPU': 1, 'TPU': 22, 'FPGA': 22, 'ASIC': 22,
    'DRAM': 1, 'HBM': 22, 'SSD': 1,
    'PostgreSQL': 7, 'MySQL': 7, 'MongoDB': 7, 'AWS S3': 9,
    'Pinecone': 4, 'Neo4j': 7, 'Redis': 12, 'Apache Kafka': 12,
    'Transformer': 1, 'CNN': 1, 'XGBoost': 7,
    'Docker': 7, 'Kubernetes': 1, 'Airflow': 7,
    'React': 4, 'FastAPI': 4, 'Chatbot': 37,
    'Prometheus': 39, 'Grafana': 39, 'SHAP': 30,
    'OAuth': 30, 'Encryption': 30, 'Model Cards': 30
  };
  
  for (const [key, approachId] of Object.entries(componentToApproach)) {
    if (componentName.includes(key)) {
      return approachId;
    }
  }
  
  // Default to first approach or Build In-House
  return approaches[0] || 1;
}

function calculateCost(components) {
  const cost = { low: 0, high: 0, breakdown: [] };
  components.forEach(comp => {
    cost.low += comp.cost?.low || 0;
    cost.high += comp.cost?.high || 0;
    cost.breakdown.push({
      component: comp.name,
      low: comp.cost?.low || 0,
      high: comp.cost?.high || 0,
      unit: comp.cost?.unit || ''
    });
  });
  return cost;
}

function generateRecommendations(req, arch) {
  const recommendations = [];
  
  if (arch.summary?.completenessScore < 70) {
    recommendations.push({
      type: 'completeness',
      message: `Solution completeness is ${arch.summary.completenessScore}%. Add more components.`,
      priority: 'High'
    });
  }
  
  if (arch.summary?.dataCoverageScore < 70) {
    recommendations.push({
      type: 'data',
      message: `Data type coverage is ${arch.summary.dataCoverageScore}%.`,
      priority: 'High'
    });
  }
  
  if (req.budget === 'low' && arch.costModel.high > 10000) {
    recommendations.push({
      type: 'cost',
      message: 'Solution cost exceeds low budget.',
      priority: 'High'
    });
  }
  
  return recommendations;
}

function identifyRisks(req, arch) {
  const risks = [];
  
  if (req.latency === 'ultra-low' && !arch.selectedComponents.some(c => c.name === 'FPGA')) {
    risks.push({
      category: 'Performance',
      risk: 'Ultra-low latency may not be achieved without FPGA',
      impact: 'High',
      probability: 'Medium',
      mitigation: 'Add FPGA or ASIC'
    });
  }
  
  if (req.compliance === 'high' && !arch.selectedComponents.some(c => c.name === 'Audit Logs (SIEM)')) {
    risks.push({
      category: 'Compliance',
      risk: 'Regulatory compliance may not be met',
      impact: 'Critical',
      probability: 'High',
      mitigation: 'Add audit logging'
    });
  }
  
  return risks;
}

function createRoadmap(req) {
  return [
    { phase: 1, name: 'Foundation', duration: '0-3 months', focus: 'Infrastructure setup' },
    { phase: 2, name: 'Development', duration: '3-6 months', focus: 'Model development' },
    { phase: 3, name: 'Deployment', duration: '6-9 months', focus: 'Production deployment' },
    { phase: 4, name: 'Optimization', duration: '9-12 months', focus: 'Continuous improvement' }
  ];
}

function defineKPIs(req) {
  return {
    technical: {
      modelAccuracy: req.priority === 'quality' ? '> 95%' : '> 85%',
      latency: req.latency === 'ultra-low' ? '< 1ms' : '< 100ms',
      uptime: '99.9%'
    },
    business: {
      roi: '> 200%',
      costReduction: '> 20%'
    },
    operational: {
      mttr: '< 1 hour',
      deploymentFrequency: 'Weekly'
    }
  };
}

function calculateAlignmentScore(req, components) {
  let score = 100;
  
  req.problemTypes?.forEach(pt => {
    const mapping = PROBLEM_TYPE_COMPONENT_MAPPING[pt];
    if (mapping) {
      const essentialCovered = mapping.essential?.filter(name => 
        components.some(c => c.name.toLowerCase() === name.toLowerCase())
      ).length || 0;
      const totalEssential = mapping.essential?.length || 1;
      if (essentialCovered / totalEssential < 0.5) score -= 20;
    }
  });
  
  return Math.max(score, 0);
}

function calculateConfidence(req) {
  let confidence = 75;
  if (req.expertise === 'expert') confidence += 10;
  if (req.expertise === 'beginner') confidence -= 5;
  return Math.min(Math.max(confidence, 0), 100);
}

function calculateCompletenessScore(req, components) {
  const coveredLayers = new Set(components.map(c => c.layerId));
  const essentialLayers = ['layer1', 'layer3', 'layer4', 'layer5', 'layer6', 'layer8', 'layer10', 'layer11'];
  return Math.round((essentialLayers.filter(l => coveredLayers.has(l)).length / essentialLayers.length) * 100);
}

function calculateDataCoverageScore(dataTypes, components) {
  if (!dataTypes || dataTypes.length === 0) return 100;
  let covered = 0;
  dataTypes.forEach(dt => {
    const mapping = DATA_TYPE_COMPONENT_MAPPING[dt];
    if (mapping?.storage?.some(name => components.some(c => c.name === name))) covered++;
  });
  return Math.round((covered / dataTypes.length) * 100);
}

// Legacy export
export const generateAutoArchitecture = generateUniversalArchitecture;

export default generateUniversalArchitecture;